import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import TechDaySelector, {
    addDays,
    dayKey,
    sameDay,
    startOfDay
} from '../../../components/technician/TechDaySelector';
import '../../admin/quotes/quote-generator.css';
import '../technician.css';
import { apiRequest } from '../../../api/client';

type Job = {
    _id: string;
    title?: string;
    status?: string;
    priority?: 'low' | 'medium' | 'high' | string;
    client?: { name?: string; address?: string };
    schedule?: { date?: string; startTime?: string; endTime?: string };
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function priorityClass(priority?: string): string {
    return priority === 'high' ? 'warn' : 'ok';
}

function JobList(): React.JSX.Element {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const today = useMemo(() => startOfDay(new Date()), []);
    const [selectedDay, setSelectedDay] = useState<Date>(today);

    useEffect(() => {
        apiRequest<{ data: Job[] }>('/api/work-orders')
            .then((data) => setJobs(data.data || []))
            .catch(() => setJobs([]))
            .finally(() => setLoading(false));
    }, []);

    /** Map of dayKey -> jobs scheduled that day, sorted by start time. */
    const jobsByDay = useMemo(() => {
        const map = new Map<string, Job[]>();
        for (const job of jobs) {
            if (!job.schedule?.date) continue;
            const d = new Date(job.schedule.date);
            if (Number.isNaN(d.getTime())) continue;
            const key = dayKey(startOfDay(d));
            const list = map.get(key) || [];
            list.push(job);
            map.set(key, list);
        }
        map.forEach((list) => {
            list.sort((a, b) => (a.schedule?.startTime || '99:99').localeCompare(b.schedule?.startTime || '99:99'));
        });
        return map;
    }, [jobs]);

    const counts = useMemo(() => {
        const m = new Map<string, number>();
        jobsByDay.forEach((list, key) => m.set(key, list.length));
        return m;
    }, [jobsByDay]);

    const unscheduledCount = useMemo(() => jobs.filter((j) => !j.schedule?.date).length, [jobs]);
    const selectedJobs = jobsByDay.get(dayKey(selectedDay)) || [];

    const selectedHeading = `${DOW[selectedDay.getDay()]}, ${MONTHS[selectedDay.getMonth()]} ${selectedDay.getDate()}`;
    const relLabel = sameDay(selectedDay, today)
        ? 'Today'
        : sameDay(selectedDay, addDays(today, 1))
          ? 'Tomorrow'
          : sameDay(selectedDay, addDays(today, -1))
            ? 'Yesterday'
            : selectedDay.getTime() < today.getTime()
              ? 'Past'
              : 'Upcoming';

    return (
        <AppShell>
            <div className="tech-page">
                <header className="tech-hero">
                    <div>
                        <p className="tech-kicker">Assignments</p>
                        <h1 className="tech-title">Job List</h1>
                        <p className="tech-sub">Pick a day to see that day&apos;s route. Swipe weeks to review past or plan ahead.</p>
                    </div>
                    <div className="tech-actions">
                        <button type="button" className="tech-btn tech-btn-primary" onClick={() => setSelectedDay(today)}>
                            Today
                        </button>
                    </div>
                </header>

                <TechDaySelector selectedDay={selectedDay} onSelectDay={setSelectedDay} counts={counts} />

                <div className="tech-day-header">
                    <div>
                        <span className="tech-day-header__rel">{relLabel}</span>
                        <h2 className="tech-day-header__title">{selectedHeading}</h2>
                    </div>
                    <span className="tech-day-header__count">
                        {selectedJobs.length} {selectedJobs.length === 1 ? 'job' : 'jobs'}
                    </span>
                </div>

                {loading ? (
                    <div className="tech-card tech-empty">
                        <p style={{ margin: 0, color: 'var(--tech-muted)' }}>Loading jobs…</p>
                    </div>
                ) : selectedJobs.length === 0 ? (
                    <div className="tech-card tech-empty">
                        <p style={{ margin: 0, color: 'var(--tech-muted)' }}>No jobs scheduled for this day.</p>
                    </div>
                ) : (
                    <div className="tech-joblist">
                        {selectedJobs.map((job) => (
                            <button
                                key={job._id}
                                type="button"
                                className="tech-jobrow"
                                onClick={() => navigate(`/technician/jobs/${job._id}`)}
                            >
                                <span className="tech-jobrow__time">
                                    {job.schedule?.startTime ? (
                                        <>
                                            <span className="tech-jobrow__time-start">{job.schedule.startTime}</span>
                                            {job.schedule.endTime ? (
                                                <span className="tech-jobrow__time-end">{job.schedule.endTime}</span>
                                            ) : null}
                                        </>
                                    ) : (
                                        <span className="tech-jobrow__time-start">—</span>
                                    )}
                                </span>
                                <span className="tech-jobrow__main">
                                    <span className="tech-jobrow__title">{job.client?.name || job.title || 'Assigned site'}</span>
                                    <span className="tech-jobrow__meta">
                                        {job.title ? <span>{job.title}</span> : null}
                                        {job.client?.address ? <span>{job.client.address}</span> : null}
                                    </span>
                                </span>
                                <span className="tech-jobrow__right">
                                    <span className={`tech-badge ${priorityClass(job.priority)}`}>
                                        {(job.priority || 'normal').toString()}
                                    </span>
                                    {job.status ? <span className="tech-jobrow__status">{job.status}</span> : null}
                                    <span className="tech-jobrow__chevron" aria-hidden="true">›</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {unscheduledCount > 0 ? (
                    <p className="tech-sub" style={{ marginTop: 16 }}>
                        {unscheduledCount} unscheduled job{unscheduledCount === 1 ? '' : 's'} not shown here — the office
                        hasn&apos;t set a date yet.
                    </p>
                ) : null}
            </div>
        </AppShell>
    );
}

export default JobList;
